import ThemeProvider from '@/theme/ThemeProvider'
import { LocaleProvider } from '@/contexts/LocaleContext'
import { AuthProvider } from '@/contexts/AuthContext'
import AppLayout from '@/layout/AppLayout'
import ProtectedRoute from '@/components/ProtectedRoute'
import OfflineBanner from '@/components/OfflineBanner'
import CookieNotice from '@/components/CookieNotice'
import ProjectsList from '@/pages/ProjectsList'
import ImageAnalysisPage from '@/pages/ImageAnalysisPage'
import LiveAnalysisSessionsPage from '@/pages/LiveAnalysisSessionsPage'
import LiveAnalysisSessionDetailPage from '@/pages/LiveAnalysisSessionDetailPage'
import LoginPage from '@/pages/LoginPage'
import RegisterPage from '@/pages/RegisterPage'
import FirstLoginPage from '@/pages/FirstLoginPage'
import SendResetLinkPage from '@/pages/SendResetLinkPage'
import ResetPasswordPage from '@/pages/ResetPasswordPage'
import ProfilePage from '@/pages/ProfilePage'
import ParticipantDashboard from '@/pages/ParticipantDashboard'
import ParticipantProjectDetail from '@/pages/ParticipantProjectDetail'
import FillQuestionnaire from '@/pages/FillQuestionnaire'
import QuestionTemplates from '@/pages/QuestionTemplates'
import TemplateEditor from '@/pages/TemplateEditor'
import RespondentBase from '@/pages/RespondentBase'
import ParticipantRecordingManagement from '@/pages/ParticipantRecordingManagement'
import ResearcherDashboard from '@/pages/ResearcherDashboard'
import AdminDashboard from '@/pages/AdminDashboard'
import AdminEyeTrackingLab from '@/pages/AdminEyeTrackingLab'
import ActivityFeedPage from '@/pages/ActivityFeedPage'
import CreateStudyWizard from '@/pages/CreateStudyWizard'
import TeamPage from '@/pages/TeamPage'
import PlansPage from '@/pages/PlansPage'
import TeamJoinPage from '@/pages/TeamJoinPage'
import StudyMonitoring from '@/pages/StudyMonitoring'
import StudyResults from '@/pages/StudyResults'
import EmotionDemo from '@/pages/EmotionDemo'
import RecordingSessionView from '@/components/RecordingSessionView'
import RespondentJourney, { RespondentResultsPage } from '@/pages/RespondentJourney'
import { useAuth } from '@/contexts/AuthContext'
import { HashRouter, Navigate, Route, Routes } from 'react-router-dom'

/**
 * Home route: every account is both researcher and respondent, so the landing
 * page follows the currently selected view (Airbnb-style toggle in the profile
 * menu) — both dashboards bring their own Affexy chrome, no shared AppLayout.
 */
function HomeRoute() {
  const { currentView } = useAuth()
  if (currentView === 'researcher') return <ResearcherDashboard />
  return <ParticipantDashboard />
}

/**
 * Root application component with authentication routing.
 */
export default function App() {
  console.log('App component rendering...')
  
  return (
    <LocaleProvider>
      <ThemeProvider>
        <OfflineBanner />
        <CookieNotice />
        <AuthProvider>
          <HashRouter>
          <Routes>
            {/* Public routes */}
            {/* Self-served respondent journey — fully public, no auth, own theme */}
            <Route path="/r/:token/results" element={<RespondentResultsPage />} />
            <Route path="/r/:token" element={<RespondentJourney />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/first-login" element={<FirstLoginPage />} />
            <Route path="/send-reset-link" element={<SendResetLinkPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            {/* Team invitation link — public: the invitee may not have an account yet */}
            <Route path="/team/join" element={<TeamJoinPage />} />
            
            {/* Protected routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <HomeRoute />
                </ProtectedRoute>
              }
            />
            {/* Researcher dashboard + wizard bring their own Affexy chrome — no AppLayout */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute requireResearcher>
                  <ResearcherDashboard />
                </ProtectedRoute>
              }
            />
            {/* Global admin analytics panel — gated by can_view_admin_panel */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            {/* Admin-only experimental eye-tracking test bench */}
            <Route
              path="/admin/eye-tracking"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminEyeTrackingLab />
                </ProtectedRoute>
              }
            />
            <Route
              path="/studies/new"
              element={
                <ProtectedRoute requireResearcher>
                  <CreateStudyWizard />
                </ProtectedRoute>
              }
            />
            {/* Plans — deliberately not requirePro: this is the screen a
                FaceWise account comes to in order to stop being one. */}
            <Route
              path="/plans"
              element={
                <ProtectedRoute requireResearcher>
                  <PlansPage />
                </ProtectedRoute>
              }
            />
            {/* Team panel — sublicences and who holds them */}
            <Route
              path="/team"
              element={
                <ProtectedRoute requireResearcher requirePro>
                  <TeamPage />
                </ProtectedRoute>
              }
            />
            {/* Activity feed — own Affexy chrome, no AppLayout */}
            <Route
              path="/activity"
              element={
                <ProtectedRoute requireResearcher requirePro>
                  <ActivityFeedPage />
                </ProtectedRoute>
              }
            />
            {/* Projects list — own Affexy chrome, no AppLayout */}
            <Route
              path="/projects"
              element={
                <ProtectedRoute requireResearcher>
                  <ProjectsList />
                </ProtectedRoute>
              }
            />
            {/* Emotion-analysis demo — own Affexy chrome, no AppLayout */}
            <Route
              path="/camera"
              element={
                <ProtectedRoute requireResearcher>
                  <EmotionDemo />
                </ProtectedRoute>
              }
            />
            <Route
              path="/recording-session"
              element={
                <ProtectedRoute requirePro>
                  <AppLayout>
                    <RecordingSessionView />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/image-analysis"
              element={
                <ProtectedRoute requireResearcher requirePro>
                  <AppLayout>
                    <ImageAnalysisPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/live-analysis"
              element={
                <ProtectedRoute requireResearcher requirePro>
                  <AppLayout>
                    <LiveAnalysisSessionsPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/live-analysis/:id"
              element={
                <ProtectedRoute requireResearcher requirePro>
                  <AppLayout>
                    <LiveAnalysisSessionDetailPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            {/* Study monitoring + results — own Affexy chrome, no AppLayout */}
            <Route
              path="/studies/:id"
              element={
                <ProtectedRoute requireResearcher>
                  <StudyMonitoring />
                </ProtectedRoute>
              }
            />
            <Route
              path="/studies/:id/results"
              element={
                <ProtectedRoute requireResearcher requirePro>
                  <StudyResults />
                </ProtectedRoute>
              }
            />
            {/* Profile — own Affexy chrome, no AppLayout */}
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/participant/dashboard"
              element={
                <ProtectedRoute>
                  <ParticipantDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/participant/projects/:projectId"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ParticipantProjectDetail />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/participant/projects/:projectId/questionnaire"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <FillQuestionnaire />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:projectId/participants/:participantId/recordings"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ParticipantRecordingManagement />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            {/* Question templates list — own Affexy chrome, no AppLayout */}
            <Route
              path="/questionnaires"
              element={
                <ProtectedRoute requireResearcher requirePro>
                  <QuestionTemplates />
                </ProtectedRoute>
              }
            />
            {/* Create / edit template — own Affexy chrome, no AppLayout */}
            <Route
              path="/questionnaires/new"
              element={
                <ProtectedRoute requireResearcher requirePro>
                  <TemplateEditor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/questionnaires/:id/edit"
              element={
                <ProtectedRoute requireResearcher requirePro>
                  <TemplateEditor />
                </ProtectedRoute>
              }
            />
            {/* Respondent address book — own Affexy chrome, no AppLayout */}
            <Route
              path="/respondents"
              element={
                <ProtectedRoute requireResearcher requirePro>
                  <RespondentBase />
                </ProtectedRoute>
              }
            />
            {/* Anything unmatched (a stale bookmark, a route the signed-in
                edition does not have) lands on the account's home instead of a
                blank page. */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </HashRouter>
        </AuthProvider>
      </ThemeProvider>
    </LocaleProvider>
  )
}